/**
 * useMusicPreferences Hook (Web)
 *
 * React hook for fetching and managing music preferences state.
 * Used in the onboarding wizard music step.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  getMusicPreferences,
  getMusicPreferencesWithFallback,
  clearMusicPreferencesCache,
  MusicPreferencesResponse,
  GenreDto,
  MoodDto,
  TempoDto,
} from '../services/music/musicPreferencesService';

/**
 * State returned by the useMusicPreferences hook
 */
export interface UseMusicPreferencesState {
  /** Music preferences data (genres, moods, tempos) */
  data: MusicPreferencesResponse | null;
  /** Whether data is currently being loaded */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Whether data is from cache (potentially stale) */
  isStale: boolean;
}

/**
 * Actions returned by the useMusicPreferences hook
 */
export interface UseMusicPreferencesActions {
  /** Refresh data from API (bypasses cache) */
  refresh: () => Promise<void>;
  /** Clear cache and refresh */
  clearCacheAndRefresh: () => Promise<void>;
}

/**
 * Return type for useMusicPreferences hook
 */
export interface UseMusicPreferencesReturn extends UseMusicPreferencesState {
  /** Available genres */
  genres: GenreDto[];
  /** Available moods */
  moods: MoodDto[];
  /** Available tempos */
  tempos: TempoDto[];
  /** Actions */
  actions: UseMusicPreferencesActions;
}

/**
 * Hook for fetching and managing music preferences.
 * Automatically fetches on mount and provides refresh functionality.
 *
 * @param autoFetch - Whether to fetch automatically on mount (default: true)
 * @returns Music preferences state and actions
 *
 * @example
 * ```tsx
 * function MusicStep() {
 *   const { genres, moods, tempos, isLoading, error, actions } = useMusicPreferences();
 *
 *   if (isLoading) return <LoadingSpinner />;
 *   if (error) return <ErrorMessage message={error} onRetry={actions.refresh} />;
 *
 *   return (
 *     <div>
 *       {genres.map(genre => (
 *         <Chip key={genre.id} label={genre.name} icon={genre.icon} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useMusicPreferences(autoFetch: boolean = true): UseMusicPreferencesReturn {
  const [data, setData] = useState<MusicPreferencesResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(autoFetch);
  const [error, setError] = useState<string | null>(null);
  const [isStale, setIsStale] = useState<boolean>(false);

  /**
   * Fetches music preferences from API or cache.
   */
  const fetchPreferences = useCallback(async (forceRefresh: boolean = false) => {
    setIsLoading(true);
    setError(null);
    setIsStale(false);

    try {
      const preferences = await getMusicPreferences(forceRefresh);
      setData(preferences);
    } catch (err) {
      console.error('Failed to fetch music preferences:', err);

      // Try fallback (stale cache)
      try {
        const fallback = await getMusicPreferencesWithFallback();
        setData(fallback);
        setIsStale(true);
      } catch {
        setError(err instanceof Error ? err.message : 'Failed to load music preferences');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoFetch) {
      fetchPreferences();
    }
  }, [autoFetch, fetchPreferences]);

  const refresh = useCallback(async () => {
    await fetchPreferences(true);
  }, [fetchPreferences]);

  const clearCacheAndRefresh = useCallback(async () => {
    clearMusicPreferencesCache();
    await fetchPreferences(true);
  }, [fetchPreferences]);

  return {
    data,
    isLoading,
    error,
    isStale,
    genres: data?.genres ?? [],
    moods: data?.moods ?? [],
    tempos: data?.tempos ?? [],
    actions: {
      refresh,
      clearCacheAndRefresh,
    },
  };
}
